import {
  ClipboardEvent,
  RefObject,
  createRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputChangeEventData,
  TextInputFocusEventData,
  TextInputKeyPressEventData,
} from 'react-native';
import {
  FloatingField,
  FloatingFieldHint,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Stack } from '@/common/components/Stack/Stack';
import { styles } from './DigitsField.styles';

export interface IDigitsFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  error?: string;
  hint?: string;
  onChangeFocus?: (fields: boolean[]) => void;
  disabled?: boolean;
  numberOfDigits?: number;
  autofocus?: boolean;
}

export interface IDigitField {
  ref: RefObject<TextInput | null>;
  focused: boolean;
  value: string;
}

export function DigitsField({
  id,
  value,
  className,
  onChange,
  onChangeFocus,
  error,
  hint,
  disabled,
  autofocus,
  numberOfDigits = 6,
}: IDigitsFieldProps) {
  const [fields, setFields] = useState<IDigitField[]>(
    Array.from({ length: numberOfDigits }).map((_, index) => ({
      ref: createRef<TextInput>(),
      value: isDigit(value[index]) ? value[index] : '',
      focused: false,
    }))
  );

  const firstFieldRef = useMemo(() => fields[0].ref, [fields]);

  const updateField = (idx: number, digit: Partial<IDigitField>) => {
    setFields((fields) => {
      return [
        ...fields.slice(0, idx),
        { ...fields[idx], ...digit },
        ...fields.slice(idx + 1),
      ];
    });
  };

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
    index: number
  ) => {
    const { key } = e.nativeEvent;
    const inputValue = fields[index].value;

    switch (key) {
      case 'ArrowRight':
        e.preventDefault();
        fields[index + 1]?.ref.current?.focus();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        fields[index - 1]?.ref.current?.focus();
        break;
      case 'Backspace':
        if (!inputValue) {
          e.preventDefault();
          fields[index - 1]?.ref.current?.focus();
        }
        break;
      default:
        if (inputValue === key) {
          e.preventDefault();
          fields[index + 1]?.ref.current?.focus();
        }
        break;
    }
  };

  const handleOnChange = (
    e: NativeSyntheticEvent<TextInputChangeEventData>,
    index: number
  ) => {
    let inputValue = e.nativeEvent.text;
    if (inputValue.match(new RegExp(`^\\d{${numberOfDigits}}$`))) {
      e.preventDefault();
      handlePaste(inputValue);
      return;
    }

    if (inputValue.length > 0 && !isDigit(inputValue)) {
      inputValue = fields[index].value ?? '';
    }

    updateField(index, {
      value: inputValue,
    });

    onChange(
      fields
        .map((field, idx) => (idx === index ? inputValue : field.value))
        .join('')
    );

    if (isDigit(inputValue)) {
      fields[index + 1]?.ref.current?.focus();
    }
  };

  const handleOnFocus = (
    e: NativeSyntheticEvent<TextInputFocusEventData>,
    index: number
  ) => {
    updateField(index, {
      focused: true,
    });
    onChangeFocus?.(fields.map((_, i) => i === index));
  };

  const handleOnBlur = (
    _e: NativeSyntheticEvent<TextInputFocusEventData>,
    index: number
  ) => {
    updateField(index, {
      focused: false,
    });
  };

  const handlePaste = (value: string) => {
    value.split('').forEach((char, idx) => {
      updateField(idx, {
        value: char,
      });
    });
    onChange(value);
  };

  useEffect(() => {
    if (autofocus && firstFieldRef.current) {
      firstFieldRef.current?.focus();
    }
  }, [firstFieldRef, autofocus]);

  const showHint = error || hint;

  const webProps =
    Platform.OS === 'web'
      ? {
          onPaste: (e: ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            const newValue = e.clipboardData.getData('text');
            handlePaste(newValue);
          },
        }
      : {};

  return (
    <Stack className={className}>
      <Stack horizontal className={styles.fields} shrink={false}>
        {fields.map((field, index) => (
          <FloatingField
            key={index}
            focused={field.focused}
            error={error}
            disabled={disabled}
            onPress={() => field.ref.current?.focus()}
            hintHidden
            className={styles.field}
            wrapperClassName={styles.fieldWrapper}
          >
            <TextInput
              id={`${id}-${index}`}
              autoComplete={Platform.OS === 'web' ? 'off' : 'one-time-code'}
              keyboardType="number-pad"
              className={styles.input}
              ref={field.ref}
              value={field.value}
              onKeyPress={(e) => handleKeyPress(e, index)}
              onChange={(e) => handleOnChange(e, index)}
              selectTextOnFocus
              onFocus={(e) => {
                handleOnFocus(e, index);
              }}
              onBlur={(e) => {
                handleOnBlur(e, index);
              }}
              aria-invalid={!!error}
              aria-labelledby={`${id}-label`}
              allowFontScaling={false}
              {...webProps}
            />
          </FloatingField>
        ))}
      </Stack>
      {showHint && (
        <FloatingFieldHint invalid={!!error}>{error || hint}</FloatingFieldHint>
      )}
    </Stack>
  );
}

const isDigit = (char?: string) => char?.match(/^\d{1}$/) ?? false;
